const HTTP_PROTOCOL_RE = /^https?:\/\//i;
const ABSOLUTE_PROTOCOL_RE = /^[a-z][a-z\d+.-]*:\/\//i;
const BARE_HOST_RE = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?::\d+)?(?:[/?#]|$)/i;

export function looksLikeHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return normalizeValidHttpUrl(trimmed) !== null;
}

export function normalizeHttpUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (ABSOLUTE_PROTOCOL_RE.test(trimmed)) return trimmed;
  if (BARE_HOST_RE.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

export function normalizeValidHttpUrl(value: string): string | null {
  const normalized = normalizeHttpUrl(value);
  if (!HTTP_PROTOCOL_RE.test(normalized)) return null;

  try {
    const parsed = new URL(normalized);
    if (!parsed.hostname || !['http:', 'https:'].includes(parsed.protocol)) return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function getAppOrigin(fallbackHost = 'https://atrivial.com'): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return fallbackHost;
}

/**
 * Normalizes an application path for SEO canonical URLs.
 * Strips trailing slashes on all paths except the site root (`/`).
 * Preserves query strings and hash fragments.
 */
export function normalizeCanonicalPath(path: string): string {
  if (!path) return '/';

  const match = path.match(/^([^?#]*)(.*)$/);
  if (!match) return '/';

  let pathname = match[1];
  const suffix = match[2];

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }

  if (pathname !== '/') {
    pathname = pathname.replace(/\/+$/, '');
  }

  return `${pathname}${suffix}`;
}

export function getFullUrl(path: string, fallbackHost = 'https://atrivial.com'): string {
  // SEO canonical URLs and Structured Data must always use the canonical host
  // to avoid hydration mismatches between SSR pre-rendering and client-side hydration,
  // and to ensure crawlers receive correct canonical URLs regardless of current domain/iframe context.
  const normalizedPath = normalizeCanonicalPath(path.startsWith('/') ? path : `/${path}`);
  return `${fallbackHost}${normalizedPath}`;
}

/** Absolute canonical URL for `<link rel="canonical">`, sitemap `<loc>`, and JSON-LD. */
export function getCanonicalUrl(path: string, fallbackHost = 'https://atrivial.com'): string {
  return getFullUrl(path, fallbackHost);
}
